
import { X, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

interface ErrorModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  message: string
  details?: Record<string, string[]>
}

export function ErrorModal({
  isOpen,
  onClose,
  title = 'An error occurred',
  message,
  details,
}: ErrorModalProps) {
  const [showDetails, setShowDetails] = useState(false)

  if (!isOpen) return null

  const hasDetails = details && Object.keys(details).length > 0

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-[2px] animate-in fade-in duration-200">
      <Card className="w-full max-w-md shadow-2xl border-destructive/50 bg-background">
        <CardHeader className="flex flex-row items-start justify-between pb-2 space-y-0">
          <div className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="w-5 h-5" />
            <CardTitle className="text-lg font-semibold leading-none tracking-tight">
              {title}
            </CardTitle>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="w-6 h-6 -mt-1 text-muted-foreground hover:text-foreground"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4 pt-4">
          <p className="text-sm font-medium text-muted-foreground">
            {message}
          </p>

          {hasDetails && (
            <div className="mt-4 border rounded-md bg-muted/30">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="flex items-center justify-between w-full px-3 py-2 text-xs font-medium text-left border-b bg-muted/50 hover:bg-muted/70 transition-colors"
              >
                <span>Technical Details</span>
                {showDetails ? (
                  <ChevronUp className="w-3 h-3" />
                ) : (
                  <ChevronDown className="w-3 h-3" />
                )}
              </button>
              
              {showDetails && (
                <div className="p-3 space-y-2 overflow-y-auto max-h-[200px] text-xs font-mono">
                  {Object.entries(details).map(([field, errors]) => (
                    <div key={field} className="grid gap-1">
                      <span className="font-semibold text-foreground/80">{field}:</span>
                      <ul className="pl-4 list-disc text-muted-foreground">
                        {errors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button variant="default" onClick={onClose} className="w-full sm:w-auto">
              Understood
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
